import type { CSSProperties } from 'react';

/**
 * Hero (באנר ראשי) styling, shared by the builder canvas/preview
 * (BlockRenderer) and the live page (PublicCampaign) so the two can't drift.
 *
 * The image layer and the two text layers are styled INDEPENDENTLY:
 *  - imageBrightness → a CSS filter on the image div only, so darkening a
 *    photo for legibility never dims the title/subtitle with it.
 *  - titleOpacity / subtitleOpacity → the text "brightness", per text.
 * Every prop is optional; the fallbacks reproduce the legacy hero exactly
 * (title = style.fontSize/fontWeight/textColor, subtitle = 55% of the title
 * size at 90% opacity, 8px apart), so untouched banners render unchanged.
 */

type AnyProps = Record<string, any>;

const clamp = (n: number, min: number, max: number) => Math.max(min, Math.min(max, n));

/** CSS `filter` for the hero image layer (undefined = no filter at all). */
export function heroImageFilter(p: AnyProps): string | undefined {
  const b = p?.imageBrightness;
  if (typeof b !== 'number' || b === 100) return undefined;
  return `brightness(${clamp(b, 0, 300) / 100})`;
}

/**
 * The `background-image` for the hero's image layer.
 *
 * `style.bgTransparent` means "no background COLOUR" — it must NOT delete the
 * photo. It used to blank this layer too, so turning the background
 * transparent made the uploaded image vanish (a slideshow of 2+ images kept
 * working, which is what made it look random). A real image therefore always
 * renders; only the gradient fallback (no image) still honours bgTransparent,
 * so hero blocks that relied on "transparent hides my gradient" are unchanged.
 */
export function heroBackgroundImage(
  s: AnyProps,
  heroBg: string | undefined,
  currentImage: string | undefined,
): string {
  if (currentImage) return heroBg || 'none';
  return s?.bgTransparent ? 'none' : (heroBg || 'none');
}

/** 0-100 → opacity for the image layer (image over the bg colour). */
export function heroImageOpacity(p: AnyProps): number {
  return typeof p?.imageOpacity === 'number' ? clamp(p.imageOpacity, 0, 100) / 100 : 1;
}

/** Title + subtitle styles. `scale` is the canvas zoom factor (1 on the page). */
export function heroTextStyles(p: AnyProps, s: AnyProps, scale = 1): {
  title: CSSProperties;
  subtitle: CSSProperties;
} {
  const baseColor = p?.textColor || s?.textColor || '#fff';
  const titleSize = p?.titleFontSize ?? s?.fontSize ?? 36;
  const subtitleSize = p?.subtitleFontSize ?? titleSize * 0.55;
  const shadow = p?.textShadow ? '0 2px 12px rgba(0,0,0,0.55)' : undefined;
  return {
    title: {
      fontSize: `${titleSize * scale}px`,
      fontWeight: p?.titleFontWeight ?? s?.fontWeight ?? 'bold',
      color: p?.titleColor || baseColor,
      opacity: typeof p?.titleOpacity === 'number' ? clamp(p.titleOpacity, 0, 100) / 100 : undefined,
      marginBottom: p?.subtitleGap ?? 8,
      textShadow: shadow,
    },
    subtitle: {
      fontSize: `${subtitleSize * scale}px`,
      color: p?.subtitleColor || baseColor,
      opacity: (typeof p?.subtitleOpacity === 'number' ? clamp(p.subtitleOpacity, 0, 100) : 90) / 100,
      textShadow: shadow,
    },
  };
}
